import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PropertyCostsInput, PropertyInput, PropertyUpdate } from '@app/shared';
import {
  archiveProperty,
  createProperty,
  fetchProperties,
  fetchProperty,
  fetchPropertyCosts,
  savePropertyCosts,
  updateProperty,
} from './api';

const propertiesKey = ['properties'] as const;
const propertyKey = (id: number) => [...propertiesKey, id] as const;
const propertyCostsKey = (id: number) => [...propertyKey(id), 'costs'] as const;

export const propertiesQueryOptions = (includeArchived = false) =>
  queryOptions({
    queryKey: [...propertiesKey, { includeArchived }] as const,
    queryFn: () => fetchProperties(includeArchived),
  });

export const propertyQueryOptions = (id: number) =>
  queryOptions({ queryKey: propertyKey(id), queryFn: () => fetchProperty(id) });

export const propertyCostsQueryOptions = (id: number) =>
  queryOptions({ queryKey: propertyCostsKey(id), queryFn: () => fetchPropertyCosts(id) });

export function useProperties(includeArchived = false) {
  return useQuery(propertiesQueryOptions(includeArchived));
}

export function useProperty(id: number) {
  return useQuery(propertyQueryOptions(id));
}

export function usePropertyCosts(id: number) {
  return useQuery(propertyCostsQueryOptions(id));
}

export function useCreateProperty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PropertyInput) => createProperty(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: propertiesKey }),
  });
}

export function useUpdateProperty(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PropertyUpdate) => updateProperty(id, input),
    onSuccess: (property) => {
      queryClient.setQueryData(propertyKey(id), property);
      queryClient.invalidateQueries({ queryKey: propertiesKey });
    },
  });
}

export function useArchiveProperty(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (archived: boolean) => archiveProperty(id, archived),
    onSuccess: (property) => {
      queryClient.setQueryData(propertyKey(id), property);
      queryClient.invalidateQueries({ queryKey: propertiesKey });
    },
  });
}

export function useSavePropertyCosts(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PropertyCostsInput) => savePropertyCosts(id, input),
    onSuccess: (costs) => queryClient.setQueryData(propertyCostsKey(id), costs),
  });
}
